import React, { useState, useEffect, useRef } from "react";

// Complete Knowledge Base with all products & multi-channel quote options
const KNOWLEDGE = {
  welcome: {
    text: "👋 Hello! Welcome to **Life Care RO Systems** (Dwarka, Delhi).\n\nI can help you explore all our products, get custom price quotes, book doorstep service, or answer your questions. What would you like to explore?",
    options: [
      { id: "water_purifiers", label: "💧 Explore All RO Water Purifiers" },
      { id: "air_purifier", label: "🌬️ 7-Stage Air Purifier" },
      { id: "quote_options_general", label: "💰 Get Price Quote / Inquire" },
      { id: "ro_service", label: "👨‍🔧 Book RO Service / AMC" },
      { id: "commercial_ro", label: "🏭 Commercial RO & DM Plants" },
      { id: "location_contact", label: "📍 Showroom & Contact Info" },
    ],
  },
  water_purifiers: {
    text: "💧 **All Life Care RO Water Purifier Models:**\n\n1. **Wall Mounted Storage RO** (15L Anti-Bacterial Tank, 7-Stage Alkaline Filtration, available in White & Sleek Matte Black).\n2. **Under-Sink Model** (100% hidden under modular kitchen cabinets with luxury chrome swan-neck tap).\n3. **Small Storage RO** (10L compact space-saver for apartments).\n4. **RO 75 GPD** (High-durability workhorse home purifier with TDS controller).\n5. **DM Plant** (Industrial Demineralization System for zero-conductivity pure water).\n6. **Commercial RO Plant** (500 to 5,000+ LPH high-capacity plants for cafes, hospitals, offices & industries).\n\n✨ *All models include Free Doorstep Installation, 1-Year Comprehensive Warranty & Direct Factory Pricing.*",
    options: [
      { id: "wall_mounted_detail", label: "🔹 Wall Mounted RO Details" },
      { id: "undersink_detail", label: "🔹 Under-Sink RO Details" },
      { id: "small_ro_detail", label: "🔹 Small Storage RO Details" },
      { id: "ro75_detail", label: "🔹 RO 75 GPD Details" },
      { id: "dm_plant_detail", label: "🔹 DM Industrial Plant Details" },
      { id: "commercial_ro", label: "🔹 Commercial RO Plant Details" },
      { id: "quote_options_water", label: "💰 Get Quote / Best Price" },
      { id: "goto_water", label: "🌐 View All on Website" },
      { id: "main_menu", label: "↩️ Back to Main Menu" },
    ],
  },
  wall_mounted_detail: {
    text: "✨ **1. Wall Mounted Storage RO Specifications:**\n\n• **Storage:** 15 Liters food-grade anti-bacterial tank\n• **Purification:** 7-Stage (Pre-Filter, Sediment, Pre-Carbon, RO Membrane, Post-Carbon, UV/UF, Alkaline Mineralizer)\n• **Variants:** Premium White & Sleek Matte Black Edition\n• **Features:** Real-time LED status indicators, auto-flush system, low power consumption\n• **Warranty:** 1-Year Full Warranty + Free Doorstep Installation.",
    options: [
      { id: "quote_wall_ro", label: "💰 Get Quote for Wall RO" },
      { id: "water_purifiers", label: "↩️ Back to All Products" },
      { id: "main_menu", label: "🏠 Main Menu" },
    ],
  },
  undersink_detail: {
    text: "✨ **2. Under-Sink RO Model (Modular Kitchens):**\n\n• **Concealed Design:** 100% hidden inside your wooden cabinet under the sink\n• **Luxury Faucet:** Slim stainless steel / chrome swan-neck tap mounted on the countertop\n• **Tank:** External high-pressure anti-bacterial hydro-pneumatic storage tank\n• **Benefits:** Keeps kitchen walls and countertops 100% clean and clutter-free!",
    options: [
      { id: "quote_undersink", label: "💰 Get Quote for Under-Sink RO" },
      { id: "water_purifiers", label: "↩️ Back to All Products" },
      { id: "main_menu", label: "🏠 Main Menu" },
    ],
  },
  small_ro_detail: {
    text: "✨ **3. Small Storage RO (Compact Apartment Purifier):**\n\n• **Storage:** 10 Liters compact storage tank\n• **Filtration:** 5-Stage comprehensive RO purification\n• **Design:** Space-saving sleek wall-mount for small kitchens & studio apartments\n• **Benefits:** Budget-friendly, reliable, and easy to maintain.",
    options: [
      { id: "quote_small_ro", label: "💰 Get Quote for Small RO" },
      { id: "water_purifiers", label: "↩️ Back to All Products" },
      { id: "main_menu", label: "🏠 Main Menu" },
    ],
  },
  ro75_detail: {
    text: "✨ **4. RO 75 GPD (Classic Heavy-Duty Home Purifier):**\n\n• **Capacity:** 75 Gallons Per Day high-flow purification\n• **Filtration:** 6-Stage purification with TDS controller\n• **Membrane:** High-rejection membrane for high TDS water (removes heavy metals, bacteria, viruses)\n• **Warranty:** 1-Year Comprehensive Warranty.",
    options: [
      { id: "quote_ro75", label: "💰 Get Quote for RO 75 GPD" },
      { id: "water_purifiers", label: "↩️ Back to All Products" },
      { id: "main_menu", label: "🏠 Main Menu" },
    ],
  },
  dm_plant_detail: {
    text: "✨ **5. Demineralization (DM) Industrial Plant:**\n\n• **System:** Dual-bed (Cation & Anion) industrial resin water purification\n• **Output:** Zero-conductivity demineralized high-purity water\n• **Applications:** Laboratories, Battery manufacturing, Pharmaceuticals, Boiler feed water\n• **Features:** Manual & Auto-regeneration multi-port valves.",
    options: [
      { id: "quote_dm", label: "💰 Get Quote for DM Plant" },
      { id: "water_purifiers", label: "↩️ Back to All Products" },
      { id: "main_menu", label: "🏠 Main Menu" },
    ],
  },
  commercial_ro: {
    text: "🏭 **6. Commercial & Industrial RO Plants (500 to 5,000+ LPH):**\n\n• **Capacities:** 500 LPH, 1,000 LPH, 2,000 LPH up to 5,000+ LPH\n• **Design:** Heavy-duty stainless steel skid frame, FRP vessels, high-pressure vertical pump & digital control box\n• **Ideal For:** Corporate Offices, Cafes, Restaurants, Hospitals, Diagnostic Labs, Hostels & Factories\n• **Benefits:** Eliminates expensive 20L plastic cans, providing pure mineral-balanced water on-demand.",
    options: [
      { id: "quote_commercial", label: "💰 Get Commercial Plant Quote" },
      { id: "water_purifiers", label: "↩️ Back to All Products" },
      { id: "main_menu", label: "🏠 Main Menu" },
    ],
  },
  air_purifier: {
    text: "🌬️ **7. Life Care Next-Gen 7-Stage HEPA Air Purifier:**\n\n• **Price:** ₹18,999*\n• **Coverage:** Up to 500 sq.ft (CADR 350 m³/h)\n• **Filtration Stages:**\n  1. Pre-Filter (Hair & coarse dust)\n  2. Medical True HEPA H13 (99.97% of PM2.5 / 0.3μm)\n  3. Activated Carbon (Smoke, odors, VOCs)\n  4. Cold Catalyst (Formaldehyde)\n  5. UV-C Sterilization (Pathogens & bacteria)\n  6. Negative Ionizer (Revitalizing fresh air)\n  7. Smart Sensor with Real-Time Digital AQI Display\n• **Features:** Smart Remote, Auto Mode, Ultra-Quiet Sleep Mode.",
    options: [
      { id: "quote_air", label: "💰 Get Quote for Air Purifier" },
      { id: "goto_air", label: "🌐 View on Website" },
      { id: "goto_brochure", label: "📄 Download Brochure" },
      { id: "main_menu", label: "↩️ Back to Main Menu" },
    ],
  },
  ro_service: {
    text: "👨‍🔧 **RO Repair, Doorstep Service & AMC Maintenance:**\n\n• **Doorstep Service:** Same-day technician visit across Dwarka (Sectors 1 to 23) and Delhi NCR.\n• **Services Provided:**\n  - Complete Filter Replacement (Sediment, Carbon, RO Membrane, UV)\n  - TDS & Purity Quality Inspection\n  - Pump & Electrical Repair\n  - Annual Maintenance Contracts (AMC) with year-round replacement guarantee.",
    options: [
      { id: "quote_service", label: "💰 Book Service / Get AMC Quote" },
      { id: "call_technician", label: "📞 Call Helpline (+91 9312670679)" },
      { id: "main_menu", label: "↩️ Back to Main Menu" },
    ],
  },
  location_contact: {
    text: "📍 **Life Care RO Systems Showroom & Helpline:**\n\n• **Address:** T-19, Malik Buildcon Plaza-2, Sector-12, Dwarka, New Delhi - 110075\n• **Helpline 1:** +91 9312670679\n• **Helpline 2:** +91 9212213717\n• **Email:** care.lifecarerosystems@gmail.com\n• **Instagram:** @lifecarerosystems\n• **Hours:** Monday to Sunday: 9:00 AM – 9:00 PM\n• **Service Areas:** Dwarka (Sectors 1–23), Janakpuri, Vikaspuri, Uttam Nagar, Palam, Gurugram, Delhi NCR.",
    options: [
      { id: "open_google_maps", label: "🗺️ Google Maps Directions" },
      { id: "call_technician", label: "📞 Call Showroom Directly" },
      { id: "goto_contact_direct", label: "📝 Fill Website Contact Form" },
      { id: "email_general", label: "✉️ Send Official Email" },
      { id: "main_menu", label: "↩️ Back to Main Menu" },
    ],
  },
};

export default function ChatBot({ setPage, darkMode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: KNOWLEDGE.welcome.text,
      options: KNOWLEDGE.welcome.options,
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [hasUnread, setHasUnread] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isOpen, messages]);

  const addMessage = (sender, text, options = null) => {
    setMessages((prev) => [...prev, { sender, text, options }]);
  };

  // Helper to render multi-channel quote options (WhatsApp, Email, Contact Page, Phone)
  const showQuoteChannels = (productTitle, defaultBody) => {
    addMessage(
      "bot",
      `💰 **How would you like to receive your quote for ${productTitle}?**\n\nChoose your preferred channel below:`,
      [
        { id: `whatsapp_quote_${productTitle}`, label: "💬 Get Quote on WhatsApp" },
        { id: `email_quote_${productTitle}`, label: "✉️ Request Quote via Email" },
        { id: `contact_page_quote_${productTitle}`, label: "📝 Fill Website Contact Form" },
        { id: "call_technician", label: "📞 Call Helpline (+91 9312670679)" },
        { id: "water_purifiers", label: "↩️ Explore Other Products" },
        { id: "main_menu", label: "🏠 Main Menu" },
      ]
    );
  };

  const handleOptionClick = (optionId) => {
    // 1. Trigger Multi-Channel Quote Hub
    if (optionId.startsWith("quote_")) {
      let prodName = "RO Water Purifier";
      if (optionId === "quote_wall_ro") prodName = "Wall Mounted Storage RO (White/Black)";
      if (optionId === "quote_undersink") prodName = "Under-Sink RO (Modular Kitchen)";
      if (optionId === "quote_small_ro") prodName = "Small Storage RO (10L)";
      if (optionId === "quote_ro75") prodName = "RO 75 GPD Purifier";
      if (optionId === "quote_dm") prodName = "Industrial DM Plant";
      if (optionId === "quote_commercial") prodName = "Commercial RO Plant (500-5000 LPH)";
      if (optionId === "quote_air") prodName = "Next-Gen 7-Stage HEPA Air Purifier";
      if (optionId === "quote_service") prodName = "Doorstep RO Service / AMC Package";
      if (optionId === "quote_options_water" || optionId === "quote_options_general") prodName = "Life Care Products";

      addMessage("user", `I want a quote for ${prodName}`);
      showQuoteChannels(prodName);
      return;
    }

    // 2. Channel Action: WhatsApp
    if (optionId.startsWith("whatsapp_quote_")) {
      const prod = optionId.replace("whatsapp_quote_", "");
      const text = `Hi Life Care RO Systems, I would like to get the best price quote and installation details for: ${prod}.`;
      window.open(`https://wa.me/919312670679?text=${encodeURIComponent(text)}`, "_blank");
      addMessage("user", "💬 Request Quote on WhatsApp");
      addMessage("bot", `Opening WhatsApp chat with Life Care sales team (+91 9312670679) for **${prod}**...`, [
        { id: "water_purifiers", label: "💧 Explore Products" },
        { id: "main_menu", label: "🏠 Main Menu" },
      ]);
      return;
    }

    // 3. Channel Action: Email
    if (optionId.startsWith("email_quote_") || optionId === "email_general") {
      const prod = optionId.replace("email_quote_", "");
      const subject = encodeURIComponent(`Quotation & Inquiry Request: ${prod || "Life Care Products"}`);
      const body = encodeURIComponent(
        `Hi Life Care RO Systems Team,\n\nI am interested in getting a price quotation and product details for:\nProduct: ${prod || "Water / Air Purifier"}\n\nMy Details:\nName:\nContact Number:\nDelivery / Installation Area (Dwarka / Delhi NCR):\n\nPlease share the best price and installation timeline.\n\nThank you!`
      );
      window.open(`mailto:care.lifecarerosystems@gmail.com?subject=${subject}&body=${body}`, "_blank");
      addMessage("user", "✉️ Request Quote via Email");
      addMessage("bot", `Opening your email client to send request to **care.lifecarerosystems@gmail.com** for **${prod}**...`, [
        { id: "water_purifiers", label: "💧 Explore Products" },
        { id: "main_menu", label: "🏠 Main Menu" },
      ]);
      return;
    }

    // 4. Channel Action: Website Contact Page
    if (optionId.startsWith("contact_page_quote_") || optionId === "goto_contact_direct") {
      setPage("contact");
      addMessage("user", "📝 Fill Website Contact Form");
      addMessage("bot", "Navigated directly to the website's Contact & Booking Form! You can enter your phone number and request a fast callback.", [
        { id: "main_menu", label: "🏠 Main Menu" },
      ]);
      return;
    }

    // 5. Website Navigation Actions
    if (optionId === "goto_water") {
      setPage("water");
      addMessage("user", "Show me all Water Purifiers on website");
      addMessage("bot", "Navigating to the Water Purifiers collection page! Let me know if you need specs on any specific model.", [
        { id: "quote_options_water", label: "💰 Get Price Quote" },
        { id: "main_menu", label: "🏠 Main Menu" },
      ]);
      return;
    }
    if (optionId === "goto_air") {
      setPage("air");
      addMessage("user", "Show me the 7-Stage Air Purifier on website");
      addMessage("bot", "Navigating to the Air Purifiers page! You can review the 7 medical filtration stages and AQI features.", [
        { id: "quote_air", label: "💰 Get Air Purifier Quote" },
        { id: "main_menu", label: "🏠 Main Menu" },
      ]);
      return;
    }
    if (optionId === "goto_brochure") {
      setPage("brochure");
      addMessage("user", "Download technical product brochures");
      addMessage("bot", "Navigating to the Brochure download page!", [
        { id: "main_menu", label: "🏠 Main Menu" },
      ]);
      return;
    }

    // 6. Direct Phone & Maps Actions
    if (optionId === "call_technician") {
      window.location.href = "tel:9312670679";
      addMessage("user", "📞 Call Helpline");
      addMessage("bot", "Calling Life Care helpline (+91 9312670679)...", [
        { id: "main_menu", label: "🏠 Main Menu" },
      ]);
      return;
    }
    if (optionId === "open_google_maps") {
      window.open("https://www.google.com/maps/place//@28.5944266,77.0477076,17z/data=!3m1!4b1!4m3!3m2!1s0x390d1ada8a7e6a8f:0xe381c3a31f64a03c!12e1", "_blank");
      addMessage("user", "🗺️ Open Google Maps");
      addMessage("bot", "Opening Google Maps directions for T-19 Malik Buildcon Plaza-2, Sector-12 Dwarka...", [
        { id: "main_menu", label: "🏠 Main Menu" },
      ]);
      return;
    }

    // 7. General Knowledge Menu Lookup
    const target = optionId === "main_menu" ? "welcome" : optionId;
    const item = KNOWLEDGE[target];
    if (item) {
      let userLabel = "More Info";
      for (const k in KNOWLEDGE) {
        const found = KNOWLEDGE[k].options?.find((o) => o.id === optionId);
        if (found) userLabel = found.label;
      }
      addMessage("user", userLabel);
      addMessage("bot", item.text, item.options);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const query = inputValue.toLowerCase().trim();
    addMessage("user", inputValue);
    setInputValue("");

    // Intelligent Keyword Matcher
    setTimeout(() => {
      if (query.includes("quote") || query.includes("price") || query.includes("cost") || query.includes("rate") || query.includes("buy") || query.includes("order")) {
        showQuoteChannels("Life Care RO & Air Systems");
      } else if (query.includes("air") || query.includes("hepa") || query.includes("pollution") || query.includes("pm2.5")) {
        addMessage("bot", KNOWLEDGE.air_purifier.text, KNOWLEDGE.air_purifier.options);
      } else if (query.includes("service") || query.includes("repair") || query.includes("filter") || query.includes("amc") || query.includes("tds") || query.includes("technician")) {
        addMessage("bot", KNOWLEDGE.ro_service.text, KNOWLEDGE.ro_service.options);
      } else if (query.includes("commercial") || query.includes("plant") || query.includes("lph") || query.includes("dm") || query.includes("industrial") || query.includes("office")) {
        addMessage("bot", KNOWLEDGE.commercial_ro.text, KNOWLEDGE.commercial_ro.options);
      } else if (query.includes("under") || query.includes("sink") || query.includes("modular") || query.includes("cabinet")) {
        addMessage("bot", KNOWLEDGE.undersink_detail.text, KNOWLEDGE.undersink_detail.options);
      } else if (query.includes("75") || query.includes("classic") || query.includes("gpd")) {
        addMessage("bot", KNOWLEDGE.ro75_detail.text, KNOWLEDGE.ro75_detail.options);
      } else if (query.includes("small") || query.includes("10l") || query.includes("apartment")) {
        addMessage("bot", KNOWLEDGE.small_ro_detail.text, KNOWLEDGE.small_ro_detail.options);
      } else if (query.includes("black") || query.includes("wall") || query.includes("storage") || query.includes("purifier") || query.includes("water") || query.includes("ro")) {
        addMessage("bot", KNOWLEDGE.water_purifiers.text, KNOWLEDGE.water_purifiers.options);
      } else if (query.includes("address") || query.includes("location") || query.includes("where") || query.includes("contact") || query.includes("phone") || query.includes("number") || query.includes("dwarka") || query.includes("timing")) {
        addMessage("bot", KNOWLEDGE.location_contact.text, KNOWLEDGE.location_contact.options);
      } else {
        addMessage(
          "bot",
          "I can provide complete details on **All 6 RO Water Purifiers**, **7-Stage Air Purifiers**, **Doorstep RO Service**, or connect you directly with our sales team via **WhatsApp, Email, or Contact Form**!",
          KNOWLEDGE.welcome.options
        );
      }
    }, 300);
  };

  const formatText = (text) => {
    return text.split("\n").map((line, i) => {
      const parts = line.split(/(\*\*.*?\*\*|\*.*?\*)/g);
      return (
        <span key={i} style={{ display: "block", marginBottom: line ? 3 : 6 }}>
          {parts.map((part, pi) => {
            if (part.startsWith("**") && part.endsWith("**")) {
              return <strong key={pi}>{part.slice(2, -2)}</strong>;
            }
            if (part.startsWith("*") && part.endsWith("*")) {
              return <em key={pi}>{part.slice(1, -1)}</em>;
            }
            return part;
          })}
        </span>
      );
    });
  };

  return (
    <>
      {/* FLOATING CHAT TRIGGER BUTTON (Bottom-Right) */}
      <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 10000 }}>
        {!isOpen && hasUnread && (
          <div
            style={{
              position: "absolute",
              top: -8,
              left: -8,
              background: "#EF4444",
              color: "white",
              fontSize: 10,
              fontWeight: 800,
              width: 20,
              height: 20,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 6px rgba(239,68,68,0.5)",
              animation: "pulse 2s infinite",
              zIndex: 2,
            }}
          >
            1
          </div>
        )}

        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            background: isOpen
              ? darkMode ? "#162444" : "#0A2540"
              : "linear-gradient(135deg, #00C9A7, #0A2540)",
            color: "white",
            border: "2px solid #00C9A7",
            boxShadow: "0 8px 26px rgba(0,201,167,0.4)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isOpen ? "rotate(90deg)" : "scale(1)",
          }}
          title="Chat with Life Care Assistant"
        >
          {isOpen ? (
            <span style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>✕</span>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ width: 28, height: 28 }}
            >
              <path
                fillRule="evenodd"
                d="M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .444 1.223ZM8.25 10.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm4.5 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm4.5 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </button>
      </div>

      {/* CHATBOT WINDOW MODAL */}
      {isOpen && (
        <div
          style={{
            position: "fixed",
            bottom: 96,
            right: 24,
            width: "calc(100vw - 32px)",
            maxWidth: 390,
            height: "min(580px, calc(100vh - 120px))",
            background: darkMode ? "#0E182F" : "#FFFFFF",
            borderRadius: 20,
            boxShadow: "0 18px 50px rgba(10,37,64,0.35)",
            border: `1.5px solid ${darkMode ? "#1F3054" : "#E2E8F0"}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            zIndex: 10000,
            animation: "fadeInUp 0.25s ease-out",
          }}
        >
          {/* HEADER */}
          <div
            style={{
              background: "#0A2540",
              padding: "14px 18px",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "2px solid #00C9A7",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#FFFFFF",
                  padding: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  position: "relative",
                }}
              >
                <img
                  src="/images/logo.png"
                  alt="Life Care"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
                <span
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: 10,
                    height: 10,
                    background: "#10B981",
                    border: "2px solid #0A2540",
                    borderRadius: "50%",
                  }}
                />
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13.5, color: "#FFFFFF" }}>
                  Life Care Assistant
                </div>
                <div style={{ fontSize: 11, color: "#00C9A7", display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#00C9A7" }} />
                  Online | Instant Help
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: "rgba(255,255,255,0.12)",
                border: "none",
                color: "white",
                width: 28,
                height: 28,
                borderRadius: "50%",
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ✕
            </button>
          </div>

          {/* MESSAGES BODY */}
          <div
            style={{
              flex: 1,
              padding: "16px 14px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              background: darkMode ? "#0A1224" : "#F8FAFC",
            }}
          >
            {messages.map((m, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: m.sender === "user" ? "flex-end" : "flex-start",
                }}
              >
                <div
                  style={{
                    maxWidth: "88%",
                    padding: "10px 14px",
                    borderRadius: m.sender === "user" ? "16px 16px 2px 16px" : "16px 16px 16px 2px",
                    background: m.sender === "user"
                      ? "#0A2540"
                      : darkMode ? "#15223E" : "#FFFFFF",
                    color: m.sender === "user"
                      ? "#FFFFFF"
                      : darkMode ? "#F1F5F9" : "#1E293B",
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    boxShadow: m.sender === "user"
                      ? "0 4px 12px rgba(10,37,64,0.2)"
                      : "0 2px 10px rgba(0,0,0,0.06)",
                    border: m.sender === "user"
                      ? "1px solid #00C9A7"
                      : `1px solid ${darkMode ? "#23355C" : "#E2E8F0"}`,
                  }}
                >
                  {formatText(m.text)}
                </div>

                {/* Option Buttons */}
                {m.options && m.options.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginTop: 8,
                      maxWidth: "96%",
                    }}
                  >
                    {m.options.map((opt, oi) => (
                      <button
                        key={oi}
                        onClick={() => handleOptionClick(opt.id)}
                        style={{
                          background: darkMode ? "#1C2B4C" : "#FFFFFF",
                          color: darkMode ? "#00C9A7" : "#0A2540",
                          border: `1.5px solid ${darkMode ? "#00C9A7" : "#00C9A7"}`,
                          borderRadius: 20,
                          padding: "6px 11px",
                          fontSize: 11.5,
                          fontWeight: 700,
                          cursor: "pointer",
                          textAlign: "left",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                          transition: "all 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#00C9A7";
                          e.currentTarget.style.color = "#0A2540";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = darkMode ? "#1C2B4C" : "#FFFFFF";
                          e.currentTarget.style.color = darkMode ? "#00C9A7" : "#0A2540";
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* INPUT FORM */}
          <form
            onSubmit={handleSendMessage}
            style={{
              padding: "10px 12px",
              background: darkMode ? "#0E182F" : "#FFFFFF",
              borderTop: `1px solid ${darkMode ? "#1F3054" : "#E2E8F0"}`,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <input
              type="text"
              placeholder="Ask a question or request quote..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              style={{
                flex: 1,
                padding: "9px 14px",
                borderRadius: 24,
                border: `1.5px solid ${darkMode ? "#23355C" : "#CBD5E1"}`,
                background: darkMode ? "#070D1B" : "#F8FAFC",
                color: darkMode ? "white" : "#0F172A",
                fontSize: 12.5,
                outline: "none",
              }}
            />
            <button
              type="submit"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "#00C9A7",
                color: "#0A2540",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: 15,
                boxShadow: "0 2px 8px rgba(0,201,167,0.4)",
              }}
            >
              ➤
            </button>
          </form>
        </div>
      )}
    </>
  );
}
